import React, { useState } from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiCompass,
  FiLayers,
  FiCloud,
  FiCpu,
  FiGitMerge,
  FiUsers,
  FiCheck,
} from "react-icons/fi"
import Link from "../../components/Link"
import PageMetadata from "../../components/PageMetadata"
import { translateMessageId } from "../../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  button:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  min-width: 0;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.78) 0%,
      rgba(4, 14, 24, 0.55) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`
const HeroContent = styled(Container)`
  min-height: 610px;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    max-width: 670px;
    color: #fff;
    font-size: clamp(2.75rem, 5.1vw, 4.5rem);
    font-weight: 500;
    line-height: 1.07;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 500px;
    color: #e0e6ec;
    font-size: 1.1rem;
    line-height: 1.75;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    flex-shrink: 0;
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  background: #fff;
  color: #102030 !important;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`
const SectionNav = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    padding: 1.4rem 1.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    text-decoration: none;
  }
  a:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1rem 0;
      border-left: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`
const Method = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`
const Contact = styled(Section)`
  color: #fff;
  background: #0b1b2a;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3rem;
  }
  ${Kicker} {
    color: #b4cae7;
  }
  h2 {
    color: #fff;
    max-width: 640px;
  }
  p:last-child {
    color: #d9e2eb;
    max-width: 670px;
    line-height: 1.75;
    font-size: 1.05rem;
    margin: 0;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 950px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    a {
      justify-self: start;
    }
  }
`

const FAQ = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 4rem;
  padding-top: 4rem;
  margin-top: 4rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  }
  details {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.35rem 0;
    font-size: 1.05rem;
    line-height: 1.4;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: "+";
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  details[open] summary::after {
    content: "−";
  }
  summary:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  details p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.8;
    margin: 0;
    padding: 0 2rem 1.5rem 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-top: 3rem;
    margin-top: 3rem;
  }
`
const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`
const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 3rem;
  @media (max-width: 750px) {
    grid-template-columns: 1fr;
  }
`
const Expertise = styled.article`
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: start;
  gap: 1.4rem;
  padding: 2rem 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    width: 1.8rem;
    height: 1.8rem;
  }
  h3 {
    margin-bottom: 0.75rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 400px) {
    grid-template-columns: 1.5rem 1fr;
    gap: 1rem;
    > svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`
const Focus = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const FocusGrid = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 3rem;
  align-items: start;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`
const FocusSelector = styled.div`
  display: grid;
  gap: 0.75rem;
  button {
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 1rem;
    width: 100%;
    text-align: left;
    padding: 1.5rem;
    font: inherit;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.lightBorder};
    border-left: 3px solid transparent;
  }
  button[aria-pressed="true"] {
    border-left-color: ${({ theme }) => theme.colors.primary};
  }
  button:hover,
  button[aria-pressed="true"] {
    background: ${({ theme }) => theme.colors.ednBackground};
  }
  strong {
    display: block;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 0.35rem;
  }
  small {
    display: block;
    font-size: 0.85rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.text200};
  }
  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 600px) {
    button {
      padding: 1.2rem;
    }
  }
`
const FocusPanel = styled.div`
  background: #0b1b2a;
  color: #fff;
  padding: 2.5rem;
  min-height: 430px;
  &[hidden] {
    display: none;
  }
  h3 {
    color: #fff;
    font-size: clamp(1.6rem, 2.5vw, 2rem);
    line-height: 1.25;
    margin-bottom: 1.5rem;
  }
  p {
    color: #ccd6e2;
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  > span {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    color: #a9c6ed;
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 1.75rem;
  }
  > div {
    border-top: 1px solid #3a4c60;
    margin-top: 1.75rem;
    padding-top: 1.5rem;
  }
  h4 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #a9c6ed;
    font-size: 0.8rem;
    font-weight: 500;
    margin: 0 0 0.65rem;
  }
  h4 svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    min-height: 0;
    padding: 1.5rem;
  }
`
const DeliverablesGrid = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 4.5rem;
  ${SectionIntro} {
    margin: 0;
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const Deliverables = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1.5rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding: 1.6rem 0;
    margin: 0;
  }
  li::before {
    display: none;
  }
  li:last-child {
    padding-bottom: 0;
  }
  span {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    padding-top: 0.2rem;
  }
  h3 {
    margin-bottom: 0.65rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 400px) {
    li {
      grid-template-columns: 1.5rem 1fr;
      gap: 1rem;
    }
  }
`
const FAQSection = styled(Section)`
  ${FAQ} {
    border-top: 0;
    margin-top: 0;
    padding-top: 0;
  }
`
const expertise = [
  { id: "strategy", icon: FiCompass },
  { id: "architecture", icon: FiLayers },
  { id: "cloud", icon: FiCloud },
  { id: "data", icon: FiCpu },
  { id: "delivery", icon: FiGitMerge },
  { id: "governance", icon: FiUsers },
]
const situations = ["launch", "evolve", "align"]
const deliverables = ["assessment", "options", "roadmap", "transfer"]
const steps = ["understand", "evaluate", "prioritize", "support"]
const related = [
  { id: "architecture", to: "/arquitectura/detalle/" },
  { id: "development", to: "/desarrollo/" },
  { id: "devsecops", to: "/devsecops/" },
  { id: "ai", to: "/ai-service/" },
]

const ConsultingPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`consulting-${id}`, intl)
  const [activeSituation, setActiveSituation] = useState("launch")

  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt={t("hero-alt")}
          loading="eager"
          imgStyle={{ objectPosition: "74% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("title")}</h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")} <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#enfoque">
              {t("explore")} <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </Hero>
      <SectionNav aria-label={t("navigation")}>
        <Container>
          <Link to="#servicios">
            {t("nav-services")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#enfoque">
            {t("nav-focus")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#entregables">
            {t("nav-deliverables")} <FiArrowDown aria-hidden="true" />
          </Link>
        </Container>
      </SectionNav>
      <Section id="servicios" aria-labelledby="consulting-services-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("services-eyebrow")}</Kicker>
            <h2 id="consulting-services-title">{t("services-title")}</h2>
            <p>{t("services-intro")}</p>
          </SectionIntro>
          <ExpertiseGrid>
            {expertise.map(({ id, icon: Icon }) => (
              <Expertise key={id}>
                <Icon aria-hidden="true" />
                <div>
                  <h3>{t(`${id}-title`)}</h3>
                  <p>{t(`${id}-description`)}</p>
                </div>
              </Expertise>
            ))}
          </ExpertiseGrid>
        </Container>
      </Section>
      <Focus id="enfoque" aria-labelledby="consulting-focus-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("focus-eyebrow")}</Kicker>
            <h2 id="consulting-focus-title">{t("focus-title")}</h2>
            <p>{t("focus-intro")}</p>
          </SectionIntro>
          <FocusGrid>
            <FocusSelector role="group" aria-label={t("focus-selector")}>
              {situations.map((id) => (
                <button
                  type="button"
                  key={id}
                  aria-pressed={activeSituation === id}
                  aria-controls={`focus-${id}`}
                  onClick={() => setActiveSituation(id)}
                >
                  <span>
                    <strong>{t(`focus-${id}-label`)}</strong>
                    <small>{t(`focus-${id}-hint`)}</small>
                  </span>
                  <FiArrowRight aria-hidden="true" />
                </button>
              ))}
            </FocusSelector>
            <div>
              {situations.map((id) => (
                <FocusPanel
                  key={id}
                  id={`focus-${id}`}
                  hidden={activeSituation !== id}
                  role="region"
                  aria-labelledby={`focus-${id}-title`}
                >
                  <span>
                    <FiCompass aria-hidden="true" />
                    {t("focus-decision")}
                  </span>
                  <h3 id={`focus-${id}-title`}>{t(`focus-${id}-title`)}</h3>
                  <p>{t(`focus-${id}-description`)}</p>
                  <div>
                    <h4>
                      <FiCheck aria-hidden="true" />
                      {t("focus-output")}
                    </h4>
                    <p>{t(`focus-${id}-output`)}</p>
                  </div>
                </FocusPanel>
              ))}
            </div>
          </FocusGrid>
        </Container>
      </Focus>
      <Section id="entregables" aria-labelledby="consulting-deliverables-title">
        <Container>
          <DeliverablesGrid>
            <SectionIntro>
              <Kicker>{t("deliverables-eyebrow")}</Kicker>
              <h2 id="consulting-deliverables-title">
                {t("deliverables-title")}
              </h2>
              <p>{t("deliverables-intro")}</p>
            </SectionIntro>
            <Deliverables>
              {deliverables.map((id, index) => (
                <li key={id}>
                  <span aria-hidden="true">0{index + 1}</span>
                  <div>
                    <h3>{t(`deliverable-${id}-title`)}</h3>
                    <p>{t(`deliverable-${id}-description`)}</p>
                  </div>
                </li>
              ))}
            </Deliverables>
          </DeliverablesGrid>
        </Container>
      </Section>
      <Method aria-labelledby="consulting-method-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("method-eyebrow")}</Kicker>
            <h2 id="consulting-method-title">{t("method-title")}</h2>
            <p>{t("method-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`method-${id}-title`)}</h3>
                <p>{t(`method-${id}-description`)}</p>
              </li>
            ))}
          </Steps>
          <Related aria-labelledby="consulting-related-title">
            <h2 id="consulting-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)} <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </Method>
      <FAQSection aria-labelledby="consulting-faq-title">
        <Container>
          <FAQ>
            <div>
              <Kicker>{t("faq-eyebrow")}</Kicker>
              <h2 id="consulting-faq-title">{t("faq-title")}</h2>
            </div>
            <div>
              {["start", "implementation", "engagement"].map((id) => (
                <details key={id}>
                  <summary>{t(`faq-${id}-question`)}</summary>
                  <p>{t(`faq-${id}-answer`)}</p>
                </details>
              ))}
            </div>
          </FAQ>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="consulting-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="consulting-contact-title">{t("cta-title")}</h2>
            <p>{t("cta-description")}</p>
          </div>
          <PrimaryLink to="/#contacto">
            {t("contact")} <FiArrowRight aria-hidden="true" />
          </PrimaryLink>
        </Container>
      </Contact>
    </Page>
  )
}

export default ConsultingPage

export const query = graphql`
  query ConsultingPageQuery {
    hero: file(relativePath: { eq: "consultoria/technology-advisory-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
